import {
  forwardRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect
} from 'react';
import type { PropsWithoutRef, ReactNode, Ref } from 'react';

import { Alert } from '../Badges';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as plusIcon from '../Icon/icons/plus.icon';
import * as caseSolidIcon from '../Icon/icons/case-solid.icon';
import * as barsIcon from '../Icon/icons/bars.icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import * as moreIcon from '../Icon/icons/more.icon';
import * as sunIcon from '../Icon/icons/sun.icon';
import * as moonIcon from '../Icon/icons/moon.icon';
import * as circleMixedLeftIcon from '../Icon/icons/circle-mixed-left.icon';
import * as shareIcon from '../Icon/icons/share.icon';
import {
  useI18n,
  useBreakpoint,
  useConsolidatedRef,
  useElement,
  useTransitionState,
  useEvent,
  useOuterEvent,
  useScrollToggle,
  useTheme,
  useUID,
  usePreviewManager,
  useDirection
} from '../../hooks';
import { openCloseStates } from '../../hooks/useTransitionState';
import SkipLinks from '../SkipLinks';
import type { SkipLinksProps } from '../SkipLinks';
import Progress from '../Progress';
import { registerAction } from '../ShortcutManager';
import useShortcut from '../../hooks/useShortcut';
import { debounce, getChildOfRootNode, isValidElement } from '../../utils';
import useKeyboardCommands from '../KeyboardCommands';
import Tooltip from '../Tooltip';
import Drawer from '../Drawer';
import useThemeMode from '../../hooks/useThemeMode';

import AppShellContext, { AppShellDrawerContext } from './AppShellContext';
import AppHeader from './AppHeader';
import AppShellDrawer from './Drawer';
import AppTopNav from './AppTopNav';
import {
  StyledAppMain,
  StyledBannerRegion,
  StyledMobileNav,
  mobileHeaderHeight,
  StyledMobileMoreMenuButton,
  menuButtonWidth,
  StyledMobileMoreMenu,
  StyledAppShellToggleButton,
  StyledAppShellTooltip,
  StyledSidebarNav,
  headerHeight,
  StyledShareIcon,
  topNavHeight
} from './AppShell.styles';
import type { AppShellProps, UtilItemProps } from './AppShell.types';
import { followingNodes, focusRegion } from './utils';
import AppNavigationPanel, { CaseTypes, Links } from './AppNavigationPanel';
import NavigationList from './NavigationList';

registerIcon(
  timesIcon,
  plusIcon,
  caseSolidIcon,
  barsIcon,
  caretRightIcon,
  caretLeftIcon,
  moreIcon,
  sunIcon,
  moonIcon,
  circleMixedLeftIcon,
  shareIcon
);
registerAction(
  { id: 'NextRegion', defaultKeyBinding: 'F9' },
  { id: 'PrevRegion', defaultKeyBinding: 'Shift+F9' },
  { id: 'KeyboardCommands', defaultKeyBinding: 'CommandOrControl+/' }
);

export const appShellBottomOffsetCSSVar = '--appshell-bottom-offset';
export const appShellHardBottomOffsetCSSVar = '--appshell-hard-bottom-offset';

interface TopNavDrawerProviderProps {
  children: ReactNode;
  topOffset: string;
}

function TopNavDrawerProvider({ children, topOffset }: TopNavDrawerProviderProps) {
  const [item, setItem] = useState<UtilItemProps | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Delay opening the drawer by one render pass after item is set.
  // The cosmos-core Drawer only fires onAfterOpen on a false→true open transition.
  // Batching setItem + setDrawerOpen(true) in the same event handler causes the
  // Drawer to mount with open=true from the start (no transition), so onAfterOpen
  // never fires and item.onDrawerOpen() (data fetching) is never called.
  // Setting drawerOpen in a separate useEffect gives the Drawer a proper transition.
  useEffect(() => {
    if (item) setDrawerOpen(true);
  }, [item]);

  const ctxValue = useMemo(
    () => ({
      drawerOpen,
      openItemName: item?.name,
      closeDrawer: () => setDrawerOpen(false),
      openDrawer: (next: UtilItemProps) => {
        // Spread to create a new object reference each time so useEffect([item])
        // always fires — even when the same item is re-opened after the drawer closes.
        setItem({ ...next });
      },
      refreshDrawer: (next: UtilItemProps) => {
        setItem({ ...next });
      }
    }),
    [drawerOpen, item?.name]
  );

  return (
    <AppShellDrawerContext.Provider value={ctxValue}>
      {children}
      {item && (
        <AppShellDrawer
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          content={item.drawerView}
          header={item.drawerHeader || item.name}
          topOffset={topOffset}
          onDrawerClose={() => {
            setItem(undefined);
            item.onDrawerClose?.();
          }}
          onDrawerOpen={item.onDrawerOpen}
        />
      )}
    </AppShellDrawerContext.Provider>
  );
}

const AppShell = forwardRef(function AppShell(
  props: PropsWithoutRef<AppShellProps>,
  ref: Ref<HTMLDivElement>
) {
  const t = useI18n();

  const {
    appInfo,
    contextSwitcher,
    searchInput,
    appHeader,
    searchPage,
    searchLabel = t('sitewide'),
    caseTypes,
    links,
    agent,
    banners,
    progress,
    main,
    operator,
    utils,
    headerUtils,
    helpMenu,
    cases,
    defaultExpanded = false,
    collapsedHoverMenus = false,
    shareApp,
    navOrientation = 'side-rail',
    ...restProps
  } = props;

  const { disableScroll, enableScroll } = useScrollToggle();
  const navRef = useConsolidatedRef(ref);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);
  const navId = useUID();
  const [expandCollapseEl, setExpandCollapseEl] = useElement<HTMLButtonElement>();

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileNavLinksCount, setMobileNavLinksCount] = useState(0);

  const { previewActive, setPreviewActive } = usePreviewManager();

  const focusedImperatively = useRef(false);
  const [headerEl, setHeaderEl] = useElement<HTMLDivElement>();
  const [mobileHeaderEl, setMobileHeaderEl] = useElement<HTMLDivElement>();

  const isSmallOrAbove = useBreakpoint('sm');
  const isTopBar = navOrientation === 'top-bar' && isSmallOrAbove;

  const showAppHeader = isTopBar ? isSmallOrAbove : !!appHeader && isSmallOrAbove;
  const showMainContent = showAppHeader ? !!(headerEl || mobileHeaderEl) : true;

  const openNavTimeoutId = useRef<ReturnType<typeof setTimeout>>();
  const closeNavTimeoutId = useRef<ReturnType<typeof setTimeout>>();

  const [searchContainerEl, setSearchContainerEl] = useState<HTMLElement | null>(null);

  const openKeyboardCommands = useKeyboardCommands();

  const theme = useTheme();

  const { state: sidebarNavState, set: setSidebarNavState } = useTransitionState({
    ref: navRef,
    property: 'width',
    states: openCloseStates,
    defaultState: defaultExpanded ? 'open' : 'closed'
  });

  const skipLinks: SkipLinksProps['items'] = useMemo(
    () => [
      {
        label: t('go_to_main_content'),
        target: { ariaLabel: t('main_content'), selector: 'main' }
      }
    ],
    [t]
  );

  const openNav = useCallback((delay: number = 0) => {
    if (closeNavTimeoutId.current) clearTimeout(closeNavTimeoutId.current);
    openNavTimeoutId.current = setTimeout(() => {
      setSidebarNavState('open');
    }, delay);
  }, []);

  const closeNav = useCallback((delay: number = 0) => {
    if (openNavTimeoutId.current) clearTimeout(openNavTimeoutId.current);
    closeNavTimeoutId.current = setTimeout(() => {
      setSidebarNavState('closed');
    }, delay);
  }, []);

  const onFocus = useCallback(() => {
    focusedImperatively.current = false;
  }, []);

  const mobileNavRef = useRef<HTMLDivElement>(null);

  useOuterEvent('focusin', [mobileNavRef, mobileButtonRef], () => setMobileNavOpen(false));

  useEffect(() => {
    if (mobileNavOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!navRef.current) return;
    const targetNode = getChildOfRootNode(navRef);
    if (!targetNode) return;
    if (!targetNode.style.getPropertyValue('--app-header-height')) {
      targetNode.style.setProperty('--app-header-height', showAppHeader ? headerHeight : '0');
    }
  }, [showAppHeader]);

  useEffect(() => {
    if (mobileNavOpen) {
      const onKeyDown = ({ key }: KeyboardEvent) => {
        if (key === 'Escape') setMobileNavOpen(false);
      };

      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    }
  }, [mobileNavOpen]);

  useEffect(() => {
    if (isSmallOrAbove && mobileNavOpen) setMobileNavOpen(false);
  }, [isSmallOrAbove, mobileNavOpen]);

  useLayoutEffect(() => {
    if (!mobileHeaderEl) return;

    document.body.style.setProperty(
      appShellBottomOffsetCSSVar,
      `calc(${mobileHeaderHeight} + var(--marking-height, 0rem))`
    );
    document.body.style.setProperty(appShellHardBottomOffsetCSSVar, 'var(--marking-height, 0rem)');

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        const mobileHeaderWidth = mobileHeaderEl?.offsetWidth ?? 0;
        const regionsPaddingInlinePixels = mobileHeaderEl
          ? 2 *
            Number.parseInt(
              window.getComputedStyle(mobileHeaderEl).getPropertyValue('padding-inline-start'),
              10
            )
          : 0;
        const mobileNavButton = mobileHeaderEl?.lastElementChild;
        if (mobileNavButton instanceof HTMLElement) {
          setMobileNavLinksCount(
            Math.max(
              Math.floor(
                mobileHeaderWidth / (menuButtonWidth(theme) + regionsPaddingInlinePixels)
              ) - (caseTypes?.length ? 2 : 1),
              0
            )
          );
        }
      }, 0)
    );

    resizeObserver.observe(mobileHeaderEl);

    return () => {
      document.body.style.removeProperty(appShellBottomOffsetCSSVar);
      document.body.style.removeProperty(appShellHardBottomOffsetCSSVar);
      resizeObserver.disconnect();
    };
  }, [mobileHeaderEl, caseTypes?.length]);

  const activatedEl = useRef<Element>(document.body);
  useEvent('click', e => {
    activatedEl.current = document.elementFromPoint(e.clientX, e.clientY) ?? document.body;
  });

  useShortcut('NextRegion', () => {
    const activeElement =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : activatedEl.current;
    const regionEls = followingNodes<HTMLElement>(
      document.createNodeIterator(document.body, NodeFilter.SHOW_ELEMENT, node =>
        // safe to treat as Element, as we requested to give us only elements
        (node as Element).hasAttribute('data-app-region') || node === activeElement
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP
      ),
      activeElement
    );
    // try to focus first visibleregion
    const focusedNode = regionEls.find(node => focusRegion(node));
    if (!focusedNode) (activeElement as HTMLElement).focus();
  });

  useShortcut('PrevRegion', () => {
    const activeElement =
      document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : activatedEl.current;
    const regionEls = followingNodes<HTMLElement>(
      document.createNodeIterator(document.body, NodeFilter.SHOW_ELEMENT, node =>
        // safe to treat as Element, as we requested to give us only elements
        (node as Element).hasAttribute('data-app-region') || node === activeElement
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP
      ),
      activeElement
    );
    regionEls.reverse();

    // removing active element parent from the list to avoid focusing back to the same region
    // but only if element is not region himself - in that case we want to keep its parent on the list
    // as it is different focusable region
    if (regionEls[0].contains(activeElement) && !activeElement.hasAttribute('data-app-region'))
      regionEls.splice(0, 1);
    const focusedNode = regionEls.find(node => focusRegion(node));
    if (!focusedNode) (activeElement as HTMLElement).focus();
  });

  useShortcut('KeyboardCommands', () => {
    openKeyboardCommands();
  });

  const contextSwitcherUtilItem: UtilItemProps | undefined = useMemo(() => {
    const {
      contexts,
      onContextClick,
      label: contextSwitcherLabel = t('switch_to')
    } = contextSwitcher ?? {};
    if (!showAppHeader && contexts && contexts?.length > 1 && !!onContextClick) {
      return {
        name: contextSwitcherLabel,
        primary: contextSwitcherLabel,
        visual: <Icon name='dot-9-solid' />,
        drawerView: (
          <NavigationList
            items={contexts!.map(context => ({
              ...context,
              primary: context.primary,
              'aria-current': !!context.selected,
              visual: context.visual,
              onClick: e => {
                onContextClick(context.id, e);
              }
            }))}
            singleSelect
          />
        )
      };
    }
  }, [
    showAppHeader,
    contextSwitcher?.contexts,
    contextSwitcher?.label,
    contextSwitcher?.onContextClick
  ]);

  const shareLabel = shareApp?.label ?? t('app_shell_share');

  const shareApplicationUtilItem: UtilItemProps | undefined = useMemo(() => {
    if (!showAppHeader && shareApp) {
      return {
        name: shareLabel,
        primary: shareLabel,
        visual: <StyledShareIcon name='share' />,
        onClick: shareApp.onClick
      };
    }
  }, [showAppHeader, shareApp, shareLabel]);

  const operatorInfoUtilItem: UtilItemProps | undefined = useMemo(() => {
    if (!showAppHeader && operator) {
      return {
        name: operator.name,
        primary: t('app_shell_user_account'),
        visual: operator.avatar,
        items: operator.actions
      };
    }
  }, [showAppHeader, operator]);

  const { themeMode, onThemeModeChange } = useThemeMode();

  const themeSwitcherUtilItem: UtilItemProps | undefined = useMemo(() => {
    if (!showAppHeader && onThemeModeChange) {
      let currentThemeIcon = 'circle-mixed-left'; // default for system
      if (themeMode === 'dark') {
        currentThemeIcon = 'moon';
      } else if (themeMode === 'light') {
        currentThemeIcon = 'sun';
      }

      return {
        name: t('theme_mode'),
        primary: t('theme_mode'),
        visual: <Icon name={currentThemeIcon} />,
        items: [
          {
            id: 'light',
            primary: t('theme_light'),
            visual: <Icon name='sun' />,
            onClick: () => onThemeModeChange('light')
          },
          {
            id: 'dark',
            primary: t('theme_dark'),
            visual: <Icon name='moon' />,
            onClick: () => onThemeModeChange('dark')
          },
          {
            id: 'system',
            primary: t('theme_system'),
            visual: <Icon name='circle-mixed-left' />,
            onClick: () => onThemeModeChange('system')
          }
        ]
      };
    }
  }, [showAppHeader, onThemeModeChange, themeMode]);

  const { start, end } = useDirection();

  const utilsList = [
    ...(utils ?? []),
    ...(contextSwitcherUtilItem ? [contextSwitcherUtilItem] : []),
    ...(shareApplicationUtilItem ? [shareApplicationUtilItem] : []),
    ...(operatorInfoUtilItem ? [operatorInfoUtilItem] : []),
    ...(themeSwitcherUtilItem ? [themeSwitcherUtilItem] : [])
  ];

  return (
    <AppShellContext.Provider
      value={useMemo(
        () => ({
          navOpen: sidebarNavState === 'open' || sidebarNavState === 'opening' || mobileNavOpen,
          drawerOpen,
          setDrawerOpen,
          collapsedHoverMenus,
          navState: sidebarNavState,
          focusedImperatively,
          headerEl: headerEl || mobileHeaderEl,
          appHeaderEl: headerEl ?? null,
          searchContainerEl,
          setSearchContainerEl,
          isMobileNav: !isSmallOrAbove,
          previewActive,
          setPreviewActive
        }),
        [
          sidebarNavState,
          drawerOpen,
          mobileNavOpen,
          isSmallOrAbove,
          headerEl,
          mobileHeaderEl,
          searchContainerEl,
          previewActive
        ]
      )}
    >
      <SkipLinks items={skipLinks} />
      {showAppHeader && (
        <AppHeader
          {...appInfo}
          contextSwitcher={contextSwitcher}
          searchInput={searchInput}
          searchLabel={searchLabel}
          operator={operator}
          headerUtils={headerUtils}
          helpMenu={helpMenu}
          searchPage={searchPage}
          shareApp={shareApp}
          ref={setHeaderEl}
        />
      )}
      {isTopBar && (
        <TopNavDrawerProvider
          topOffset={showAppHeader ? `calc(${headerHeight} + ${topNavHeight})` : topNavHeight}
        >
          <AppTopNav
            appInfo={appInfo}
            links={links}
            caseTypes={caseTypes}
            cases={cases}
            agent={agent}
            utils={utils}
            searchInput={searchInput}
            searchPage={searchPage}
            searchLabel={searchLabel}
          />
        </TopNavDrawerProvider>
      )}
      {!isTopBar && isSmallOrAbove && (
        <Flex
          {...restProps}
          container={{
            direction: 'column',
            justify: 'between'
          }}
          id={navId}
          as={StyledSidebarNav}
          withAppHeader={showAppHeader}
          portalAgent={!!agent}
          ref={navRef}
          data-app-region
          onFocus={onFocus}
          aria-label={t('app_shell_main_navigation_label')}
        >
          <AppNavigationPanel
            navState={sidebarNavState}
            appInfo={!showAppHeader ? appInfo : undefined}
            searchInput={!showAppHeader ? searchInput : undefined}
            searchLabel={searchLabel}
            caseTypes={caseTypes}
            links={links}
            cases={cases}
            utils={utilsList}
            onClick={() => {
              if (['closing', 'closed'].includes(sidebarNavState)) {
                openNav(0);
              } else {
                closeNav(0);
              }
            }}
            agent={agent}
          />
          <Flex container={{ justify: 'center' }}>
            <StyledAppShellToggleButton
              icon
              ref={setExpandCollapseEl}
              compact
              variant='simple'
              onClick={() => {
                setSidebarNavState(sidebarNavState === 'closed' ? 'open' : 'closed');
              }}
              aria-label={
                sidebarNavState === 'closed' ? t('expand_navigation') : t('collapse_navigation')
              }
              aria-expanded={sidebarNavState === 'open' || sidebarNavState === 'opening'}
              aria-controls={navId}
            >
              {['closed', 'closing'].includes(sidebarNavState) ? (
                <Icon name={`arrow-micro-${end}`} />
              ) : (
                <Icon name={`arrow-micro-${start}`} />
              )}
            </StyledAppShellToggleButton>

            {['open', 'closed'].includes(sidebarNavState) && (
              <Tooltip
                target={expandCollapseEl}
                placement='right'
                as={StyledAppShellTooltip}
                showDelay='none'
                hideDelay='none'
              >
                {sidebarNavState === 'closed' ? t('expand_navigation') : t('collapse_navigation')}
              </Tooltip>
            )}
          </Flex>
        </Flex>
      )}
      {!isSmallOrAbove && (
        <>
          <Flex
            as={StyledMobileNav}
            container={{ alignItems: 'center', justify: 'stretch', gap: 0.25 }}
            ref={setMobileHeaderEl}
            data-app-region
          >
            {caseTypes && caseTypes.length > 0 && <CaseTypes caseTypes={caseTypes} mobileNavBar />}
            {links && <Links links={links.slice(0, mobileNavLinksCount)} mobileNavBar />}
            <StyledMobileMoreMenuButton
              ref={mobileButtonRef}
              variant='simple'
              onClick={() => setMobileNavOpen(prev => !prev)}
              label={t('app_shell_more')}
              icon
              aria-expanded={mobileNavOpen}
              aria-controls={navId}
            >
              <Icon name='more' />
              {!mobileNavOpen &&
                utils?.some(item => (isValidElement(item) ? false : !!item.count)) && (
                  <Alert variant='urgent' />
                )}
              <span>{t('app_shell_more')}</span>
            </StyledMobileMoreMenuButton>
          </Flex>
          <Drawer
            open={mobileNavOpen}
            ref={mobileNavRef}
            onAfterClose={() => {
              setMobileNavOpen(false);
            }}
            position='fixed'
            placement='bottom'
            size={`calc(100dvh - ${mobileHeaderHeight})`}
            nullWhenClosed
          >
            <Flex
              {...restProps}
              container={{
                direction: 'column',
                justify: 'between'
              }}
              id={navId}
              as={StyledMobileMoreMenu}
              ref={ref}
              aria-label={t('app_shell_main_navigation_label')}
            >
              <AppNavigationPanel
                appInfo={appInfo}
                navState='open'
                searchInput={searchInput}
                searchLabel={searchLabel}
                links={links?.slice(mobileNavLinksCount)}
                cases={cases}
                utils={utilsList}
                agent={agent}
              />
            </Flex>
          </Drawer>
        </>
      )}
      {showMainContent && (
        <StyledAppMain
          appHeader={showAppHeader}
          previewActive={previewActive}
          headerOffset={isSmallOrAbove ? headerEl?.offsetHeight : undefined}
          $navOrientation={navOrientation}
          aria-busy={!!progress}
          data-app-region
        >
          <Progress
            visible={!!progress}
            placement='local'
            message={typeof progress === 'string' ? progress : t('loading')}
          />

          {banners && <StyledBannerRegion>{banners}</StyledBannerRegion>}
          {main}
        </StyledAppMain>
      )}
    </AppShellContext.Provider>
  );
});

export default AppShell;
